import { z } from 'zod';
import { LEDGER_ACTION } from '../constants/enums.js';
import { TRADABLE_TOKENS, TOKEN_IDS } from '../constants/tokens.js';
import {
  DecimalString,
  PositiveDecimalString,
  TokenIdSchema,
  PaginationQuerySchema,
  IsoDateString,
} from './common.js';
import { INTERNAL_CONVERT_PAIRS, isInternalConvertPair } from '../constants/convert.js';
import type { TokenId } from '../constants/tokens.js';

/**
 * Internal LedgerService input schema.
 *
 * Note: this is NOT a route schema — routes wrap this with the appropriate
 * authorisation checks and higher-level parameters. The LedgerService itself
 * treats this as its single entry point and refuses any write that does not
 * parse cleanly against this schema.
 */
export const LedgerMutationSchema = z.object({
  user_id: z.number().int().positive(),
  asset: TokenIdSchema,
  action: z.nativeEnum(LEDGER_ACTION),
  /**
   * Signed delta. Positive = credit, negative = debit. Zero is rejected by
   * the service because a no-op ledger entry would pollute the audit trail.
   */
  delta: DecimalString,
  ref: z.string().min(1).max(128),
  note: z.string().max(500).optional(),
  idempotency_key: z.string().min(8).max(64).optional(),
  operator_id: z.number().int().positive().optional(),
});
export type LedgerMutation = z.infer<typeof LedgerMutationSchema>;

/** Admin → manual balance adjust operator form payload (`user_id` comes from URL `:id`). */
export const AdminAdjustBalanceSchema = z.object({
  asset: TokenIdSchema,
  delta: DecimalString,
  note: z.string().min(3).max(500),
  /**
   * Two-phase operator form pattern: `xian=true` is a dry-run preview that
   * returns the computed `balance_after` without persisting; `xian=false`
   * is the real execution.
   */
  xian: z.boolean(),
});
export type AdminAdjustBalanceRequest = z.infer<typeof AdminAdjustBalanceSchema>;

/** User → P2P transfer. Restricted to tradable tokens. */
export const TransferSchema = z.object({
  to_username: z.string().min(3).max(32),
  asset: z.enum(TOKEN_IDS).refine((t) => TRADABLE_TOKENS.includes(t), {
    message: 'asset is not user-transferable',
  }),
  amount: PositiveDecimalString,
  memo: z.string().max(200).optional(),
});
export type TransferRequest = z.infer<typeof TransferSchema>;

const INTERNAL_CONVERT_PAIRS_DOC = INTERNAL_CONVERT_PAIRS.map(([a, b]) => `${a}→${b}`).join(', ');

/** User → same-account token conversion (1:1, whitelisted pairs). */
export const InternalConvertSchema = z
  .object({
    from_asset: TokenIdSchema.describe('Debit bucket; must form an allowed pair with `to_asset`.'),
    to_asset: TokenIdSchema.describe('Credit bucket; must form an allowed pair with `from_asset`.'),
    amount: PositiveDecimalString.describe('Amount to move 1:1 (decimal string, > 0).'),
    idempotency_key: z
      .string()
      .min(8)
      .max(64)
      .optional()
      .describe('Optional key so retries do not double-post the conversion.'),
  })
  .describe(
    `Same-user 1:1 conversion. Allowed directional pairs: ${INTERNAL_CONVERT_PAIRS_DOC}. ` +
      '`from_asset` and `to_asset` must differ and match one of these pairs.',
  )
  .superRefine((val, ctx) => {
    if (val.from_asset === val.to_asset) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'from and to must differ' });
      return;
    }
    if (!isInternalConvertPair(val.from_asset as TokenId, val.to_asset as TokenId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'conversion pair is not allowed',
        path: ['from_asset'],
      });
    }
  });
export type InternalConvertRequest = z.infer<typeof InternalConvertSchema>;

/** Admin → ledger query. */
export const LedgerListQuerySchema = PaginationQuerySchema.extend({
  user_id: z.coerce.number().int().positive().optional(),
  asset: TokenIdSchema.optional(),
  action: z.nativeEnum(LEDGER_ACTION).optional(),
  from: IsoDateString.optional(),
  to: IsoDateString.optional(),
});
export type LedgerListQuery = z.infer<typeof LedgerListQuerySchema>;
